import React from 'react';
import { Box, Button, IconButton, List, ListItemButton, Typography } from '@mui/material';
import { BaseModal } from 'ui/BaseModal';
import { SearchInput } from 'lib/SearchInput';
import { useTranslation } from 'react-i18next';
import { regionSetting } from '@pepperhq/regions';
import { getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js';
import * as lookup from 'country-code-lookup';
import { useGAHelpers, EGAEventName } from 'lib/useGAHelpers';

export const getFlagEmoji = (code: string) =>
    code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));

export const safeGetCountryCallingCode = (code: string): string | undefined => {
    if (!isSupportedCountry(code)) {
        return undefined;
    }
    return getCountryCallingCode(code);
};

export const getDefaultCountry = (supportedCodes: string[], regionCode?: string): string => {
    if (regionCode && supportedCodes.includes(regionCode)) {
        return regionCode;
    }
    return regionCode ?? 'GB';
};

export const getCountryOptions = (supportedCodes: string[]) =>
    Object.entries(regionSetting)
        .filter(([_, setting]) => supportedCodes.includes(setting.phoneNumberCode))
        .flatMap(([code, setting]) => {
            const phoneCode = safeGetCountryCallingCode(setting.phoneNumberCode);
            if (!phoneCode) {
                return [];
            }
            return [
                {
                    value: setting.phoneNumberCode,
                    flag: getFlagEmoji(setting.phoneNumberCode),
                    country: lookup.byIso(setting.phoneNumberCode)?.country ?? code,
                    phoneCode
                }
            ];
        });

interface CountryCodePickerProps {
    selectedCountry: string;
    onCountryChange: (value: string) => void;
    supportedCodes: string[];
}

export const CountryCodePicker: React.FC<CountryCodePickerProps> = ({
    selectedCountry,
    onCountryChange,
    supportedCodes
}) => {
    const { t } = useTranslation();
    const { logUserEvent } = useGAHelpers();

    const [pickerOpen, setPickerOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');

    const countryOptions = React.useMemo(() => getCountryOptions(supportedCodes), [supportedCodes]);

    const showCountryPicker = countryOptions.length > 1;

    const filteredOptions = React.useMemo(
        () =>
            countryOptions.filter(
                o =>
                    o.country.toLowerCase().includes(search.toLowerCase()) ||
                    String(o.phoneCode).includes(search)
            ),
        [countryOptions, search]
    );

    const handleOpenPicker = React.useCallback(() => {
        setPickerOpen(true);
        logUserEvent(EGAEventName.AreaCodeButtonTapped);
    }, [logUserEvent]);

    const handleClosePicker = React.useCallback(() => {
        setPickerOpen(false);
        setSearch('');
    }, []);

    const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }, []);

    const handleClearSearch = React.useCallback(() => {
        setSearch('');
    }, []);

    const handleSelectCountry = React.useCallback(
        (value: string) => () => {
            onCountryChange(value);
            setPickerOpen(false);
            setSearch('');
        },
        [onCountryChange]
    );

    const countryInfo = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
            <Typography fontSize="1.2rem">{getFlagEmoji(selectedCountry)}</Typography>
            {safeGetCountryCallingCode(selectedCountry) && (
                <Typography variant="body2">+{safeGetCountryCallingCode(selectedCountry)}</Typography>
            )}
        </Box>
    );

    if (!showCountryPicker) {
        return countryInfo;
    }

    const pickerContent = (
        <Box
            sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    '& .SearchInputwrapper': {
                        border: 'none'
                    }
                }}
            >
                <SearchInput
                    onChange={handleSearchChange}
                    onClear={handleClearSearch}
                    placeholder={t('GENERAL_SEARCH')}
                    value={search}
                    autoFocus
                />
                <Button onClick={handleClosePicker} sx={{ textTransform: 'none' }}>
                    {t('GENERAL_CANCEL')}
                </Button>
            </Box>
            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                {filteredOptions.map(({ value, flag, country, phoneCode }) => (
                    <ListItemButton
                        key={value}
                        onClick={handleSelectCountry(value)}
                        selected={value === selectedCountry}
                        sx={{
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' }
                        }}
                    >
                        <Typography fontSize="1.5rem" sx={{ mr: 2 }}>
                            {flag}
                        </Typography>
                        <Typography sx={{ flex: 1 }}>{country}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            +{phoneCode}
                        </Typography>
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpenPicker}
                aria-label="Select country code"
                sx={{
                    p: 0,
                    mr: 1,
                    opacity: 1,
                    color: 'inherit',
                    '&.Mui-disabled': { opacity: 1 },
                    '&:hover': {
                        backgroundColor: 'transparent'
                    }
                }}
            >
                {countryInfo}
            </IconButton>
            <BaseModal
                open={pickerOpen}
                onClose={handleClosePicker}
                paperSx={{ height: { xs: '90vh', md: '70vh' }, width: { md: '500px' } }}
            >
                {pickerContent}
            </BaseModal>
        </>
    );
};
